<template>
    <Modal id="payyo-manual-reconciliation-modal"
           icon="fa-solid fa-clipboard-check"
           :title="title">
        <div v-if="errorMessage"
             class="alert alert-danger">
            <p>{{ errorMessage }}</p>
        </div>

        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <div v-if="!errorMessage && !loading"
             data-testid="payyo-manual-reconciliation-modal-body">
            <div v-if="showButtons"
                 data-testid="payyo-manual-reconciliation-instructions"
                 class="alert alert-info">
                {{ l('PAYYO_MANUAL_RECONCILIATION.INSTRUCTIONS') }}
            </div>
            <div v-if="payments.length === 0"
                 data-testid="payyo-manual-reconciliation-no-payments"
                 class="alert alert-info mt-1">
                <span v-if="filterPayments">{{ l('PAYYO_MANUAL_RECONCILIATION.NO_COMPLETED_PAYMENTS') }}</span>
                <span v-else>{{ l('PAYYO_MANUAL_RECONCILIATION.NO_PAYMENTS') }}</span>
            </div>
            <table v-else
                   data-testid="payyo-manual-reconciliation-table"
                   class="table table-striped text-left table--valign-middle payyo-terminals-list">
                <thead>
                    <tr>
                        <th>
                            {{ l('PAYMENT.CREATED_AT') }}
                            <small class="block">&nbsp;</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.AMOUNT') }}
                            <small class="block">&nbsp;</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.STATUS') }}
                            <small class="block">{{ l('PAYMENT.AUTH_CODE') }}</small>
                        </th>
                        <th>
                            {{ l('PAYMENT.CARD_NUMBER') }}
                            <small class="block">&nbsp;</small>
                        </th>
                        <th></th>
                        <th v-if="showButtons"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="payment in payments"
                        :key="payment.id">
                        <td>{{ timeFilter(payment.created_at, false, true) }}</td>
                        <td>{{ currencyFilter(fromMinorUnit(payment.amount)) }}</td>
                        <td>
                            <PayyoSimpleStatusBadge :status="payment.status" />
                            <small v-if="payment?.payment_method?.card_present?.receipt?.authorization_code"
                                   class="block">
                                {{ payment.payment_method.card_present.receipt.authorization_code }}
                            </small>
                        </td>
                        <td>
                            <span v-if="payment?.payment_method?.card_present?.last4">
                                ************{{ payment?.payment_method?.card_present?.last4 }}
                            </span>
                        </td>
                        <td>
                            <button class="btn btn-info"
                                    @click="showPaymentDetails(payment)">
                                <i class="fa-solid fa-info-circle"></i>
                            </button>
                        </td>
                        <td v-if="showButtons"
                            class="text-center">
                            <!-- Depending on how often manual reconciliations are used we may want to consider
                                 handling payments that were authorised but not captured too. -->
                            <button v-if="payment.status === PayyoPaymentStatus.SUCCEEDED"
                                    class="btn btn-primary"
                                    @click="selectPayment(payment)">
                                {{ l('PAYYO_MANUAL_RECONCILIATION.PAYMENT_SELECT') }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PayyoPaymentStatus from '@/lib/PaymentMethods/Payyo/Enums/PayyoPaymentStatus';
import PayyoSimpleStatusBadge from '@/epos/vue/modals/Payment/PaymentMethods/Payyo/PayyoSimpleStatusBadge';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { fromMinorUnit } from '@/functions/math';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeFilter } from '@/vue/filters/timeFilter';

export default {
    name: 'PayyoManualReconciliationModal',

    components: {
        Modal,
        PageLoadingSpinner,
        PayyoSimpleStatusBadge,
        SquareBtn,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        title: {
            type: String,
            default: 'PAYYO_MANUAL_RECONCILIATION.MODAL_TITLE',
        },
        deviceId: {
            type: String,
            default: null,
        },
        showButtons: {
            type: Boolean,
            default: true,
        },
        filterPayments: {
            type: Boolean,
            default: true,
        },
        outstandingBalance: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            terminals: [],
            errorMessage: null,
            payments: [],
        };
    },

    computed: {
        PayyoPaymentStatus() {
            return PayyoPaymentStatus;
        },
    },

    created() {
        try {
            container.payyoDriver.init(false);
            container.payyoDriver.getDevice(this.deviceId)
                .then(async (data) => {
                    const paymentIds = data.recent_payments;

                    if (!paymentIds || paymentIds.length === 0) {
                        this.errorMessage = container.langService.getText('PAYYO_MANUAL_RECONCILIATION.NO_PAYMENTS');
                        return;
                    }

                    const promises = paymentIds.map((paymentId) => {
                        return container.payyoDriver.getPayment(paymentId).then((payment) => payment);
                    });

                    let payments = await Promise.all(promises);

                    // Note: this also includes payments that are already associated with orders. Perhaps we should
                    // do a search against DT API using payments.thirdPartyId?

                    payments = payments.map((payment) => {
                        if (
                            // Remember FAILED or CONFIRMED_FAILED are not real payment.statuses and they have to be
                            // inferred from other data. Therefore, we will just map them here for a friendlier user
                            // experience.
                            payment.status === PayyoPaymentStatus.REQUIRES_PAYMENT_METHOD
                            && payment?.last_error
                        ) {
                            payment.status = PayyoPaymentStatus.FAILED;
                        }
                        return payment;
                    });

                    const filteredPaymentStatuses = [
                        PayyoPaymentStatus.FAILED,
                        PayyoPaymentStatus.SUCCEEDED,
                    ];

                    // We aren't actually filtering at the moment. If we continue not filtering we could remove this.
                    if (this.filterPayments) {
                        payments = payments.filter((payment) => filteredPaymentStatuses.includes(payment.status));
                    }

                    this.payments = payments;
                }).catch((error) => {
                    this.errorMessage = error.message;
                })
                .finally(() => {
                    this.loading = false;
                });
        } catch (error) {
            this.errorMessage = error.message;
            this.loading = false;
        }
    },

    methods: {
        currencyFilter,
        fromMinorUnit,
        timeFilter,

        async selectPayment(payment) {
            let message = 'PAYYO_MANUAL_RECONCILIATION.PAYMENT_SELECT_CONFIRM';
            let customText = null;

            if (this.outstandingBalance !== fromMinorUnit(payment.amount)) {
                message = null;
                customText = container.langService.getText(
                    'PAYYO_MANUAL_RECONCILIATION.PAYMENT_SELECT_CONFIRM_AMOUNT',
                    {
                        outstandingBalance: this.outstandingBalance,
                        amount: fromMinorUnit(payment.amount),
                    }
                );
            }

            container.dialogService
                .confirm(message, {
                    customText,
                })
                .then((confirmed) => {
                    if (confirmed) {
                        return this.resolve(payment);
                    }
                    // The user cancelled
                    return null;
                });
        },
        showPaymentDetails(payment) {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.PAYYO_PAYMENT_DETAILS,
                {
                    payment,
                },
            );
        },
    },
};
</script>
